import React, { useEffect, useState } from 'react';
import {
  profile,
  education,
  certifications,
  activities,
  skills,
  projects,
  experience,
} from '../data/resume';

const NAV = [
  { href: '#work', label: 'Work' },
  { href: '#skills', label: 'Skills' },
  { href: '#record', label: 'Record' },
  { href: '#contact', label: 'Contact' },
];

/* The router owns the history stack, so the browser's own hash-link scroll
   never fires. Drive it ourselves instead. */
function scrollToHash(hash, behavior = 'smooth') {
  const target = hash && document.querySelector(hash);
  if (target) target.scrollIntoView({ behavior, block: 'start' });
  return Boolean(target);
}

function useHashScroll() {
  useEffect(() => {
    // Deep links (/#work, and the redirects off the old routes) land here.
    if (window.location.hash) {
      const id = window.setTimeout(() => scrollToHash(window.location.hash, 'auto'), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, []);

  return (event) => {
    const hash = event.currentTarget.getAttribute('href');
    if (scrollToHash(hash)) {
      event.preventDefault();
      window.history.replaceState(null, '', hash);
    }
  };
}

/* A project's repo link is only worth showing when it points somewhere
   more specific than the profile-level GitHub account. */
function repoLink(project) {
  return project.github && project.github !== profile.github ? project.github : null;
}

function Project({ project }) {
  const meta = [project.codename, project.year, project.status].filter(Boolean).join(' · ');
  const repo = repoLink(project);
  const { warStory } = project;

  return (
    <article className="project">
      <div className="project__head">
        <h3 className="project__title">{project.title}</h3>
        <span className="project__meta">{meta}</span>
      </div>

      <p className="project__summary">{project.summary}</p>

      {project.featured && (
        <ul className="bullets">
          {project.highlights.map((point) => (
            <li key={point}>
              <span>{point}</span>
            </li>
          ))}
        </ul>
      )}

      {warStory && (
        <div className="note">
          <div className="note__label">
            {warStory.label} — {warStory.title}
          </div>
          <p className="note__body">{warStory.body}</p>
        </div>
      )}

      <p className="project__stack">
        {project.stack.join(' · ')}
        {repo && (
          <>
            {' · '}
            <a href={repo} target="_blank" rel="noopener noreferrer">
              Repo ↗
            </a>
          </>
        )}
      </p>
    </article>
  );
}

function Clock() {
  const [time, setTime] = useState('');

  useEffect(() => {
    const tick = () =>
      setTime(
        new Date().toLocaleTimeString('en-US', {
          hour12: false,
          timeZone: 'America/Toronto',
        })
      );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return <>{time}</>;
}

function Site() {
  const onNavClick = useHashScroll();

  return (
    <div className="page">
      <div className="wrap">
        <header className="masthead">
          <div className="masthead__name">{profile.name}</div>
          <nav className="masthead__nav">
            {NAV.map((link) => (
              <a key={link.href} href={link.href} onClick={onNavClick}>
                {link.label}
              </a>
            ))}
          </nav>
        </header>

        {/* ------------------------------------------------------------ intro */}
        <section id="top" className="section section--intro">
          <h1 className="intro__name">{profile.name}</h1>
          <p className="intro__role">
            {profile.role} · {profile.location}
          </p>
          <p className="intro__tagline">{profile.tagline}</p>
          <p className="intro__summary">{profile.summary}</p>
          <p className="intro__available">
            <span className="dot" />
            {profile.available}
          </p>

          <div className="actions">
            <a
              className="btn btn--solid"
              href={profile.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Résumé ↗
            </a>
            <a className="btn" href={`mailto:${profile.email}`}>
              Email
            </a>
            <a className="btn" href={profile.github} target="_blank" rel="noopener noreferrer">
              GitHub ↗
            </a>
            <a className="btn" href={profile.linkedin} target="_blank" rel="noopener noreferrer">
              LinkedIn ↗
            </a>
          </div>
        </section>

        {/* ------------------------------------------------------------- work */}
        <section id="work" className="section section--work">
          <h2 className="section__label" style={{ marginBottom: 26 }}>
            Work
          </h2>
          {projects.map((project) => (
            <Project key={project.id} project={project} />
          ))}
        </section>

        {/* ----------------------------------------------------------- skills */}
        <section id="skills" className="section">
          <h2 className="section__label">Skills</h2>
          <div className="skills">
            {skills.map((group) => (
              <div key={group.category}>
                <h3>{group.category}</h3>
                <p>{group.items.join(' · ')}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ----------------------------------------------------------- record */}
        <section id="record" className="section">
          <h2 className="section__label">Record</h2>
          <div className="record">
            <div>
              <div className="record__head">
                <h3>{education.degree}</h3>
                <span className="record__when">{education.window}</span>
              </div>
              <p className="record__where">
                {education.stream} · {education.school} · {education.location}
              </p>
              <p className="record__courses">{education.coursework.join(' · ')}</p>
            </div>

            {certifications.map((cert) => (
              <div key={cert.name}>
                <div className="record__head">
                  <h3>{cert.name}</h3>
                  <span className={`record__when${cert.live ? ' record__when--live' : ''}`}>
                    {cert.status}
                  </span>
                </div>
                <p className="record__where">{cert.code}</p>
              </div>
            ))}

            {experience.map((job, i) => (
              <div key={`${job.org}-${job.window}`} className={i === 0 ? 'record__job' : undefined}>
                <div className="record__head">
                  <h3>
                    {job.role} — {job.org}
                  </h3>
                  <span className="record__when">
                    {job.window} · {job.location}
                  </span>
                </div>
                <ul className="bullets bullets--tight">
                  {job.points.map((point) => (
                    <li key={point}>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {activities.map((activity) => (
              <p key={activity} className="record__activity">
                {activity}
              </p>
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------- contact */}
        <section id="contact" className="section section--contact">
          <h2 className="section__label" style={{ marginBottom: 10 }}>
            Contact
          </h2>
          <p className="contact__lede">
            Questions about the lab, the cluster, or anything else — my inbox is open. Outside of
            tech I stay active through running, learning guitar, and creating visuals in
            TouchDesigner.
          </p>

          <dl className="defs">
            <dt>Email</dt>
            <dd>
              <a href={`mailto:${profile.email}`}>{profile.email}</a>
            </dd>
            <dt>GitHub</dt>
            <dd>
              <a href={profile.github} target="_blank" rel="noopener noreferrer">
                {profile.githubHandle} ↗
              </a>
            </dd>
            <dt>LinkedIn</dt>
            <dd>
              <a href={profile.linkedin} target="_blank" rel="noopener noreferrer">
                kailen-mccollum ↗
              </a>
            </dd>
            <dt>Résumé</dt>
            <dd>
              <a href={profile.resumeUrl} target="_blank" rel="noopener noreferrer">
                PDF ↗
              </a>
            </dd>
          </dl>

          <p className="colophon">
            {profile.location} · {profile.timezone} · <Clock />
          </p>
        </section>
      </div>
    </div>
  );
}

export default Site;
